import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Badge,
  IconButton,
  Button,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Close, ChevronLeft, ChevronRight, MoreVert } from '@mui/icons-material';
import { Column, WorkItem } from 'api/todo/types';
import { TaskCard } from './TaskCard';

interface KanbanColumnProps {
  column: Column;
  columns: Column[];
  tasks: WorkItem[];
  columnTotal: number;
  index: number;
  isOwner: boolean;
  isFirst: boolean;
  isLast: boolean;
  onTaskClick: (task: WorkItem) => void;
  onAddTask: () => void;
  onMenuAction: (action: 'rename' | 'delete', newName?: string) => void;
  onTaskDrop: (taskId: number, sourceColumnId: number, destColumnId: number, destIndex: number) => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  onLoadMore: () => void;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  columns,
  tasks,
  columnTotal,
  index,
  isOwner,
  isFirst,
  isLast,
  onTaskClick,
  onAddTask,
  onMenuAction,
  onTaskDrop,
  onMoveLeft,
  onMoveRight,
  onLoadMore,
}) => {
  const columnRef = useRef<HTMLDivElement>(null);
  const taskContainerRef = useRef<HTMLDivElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingName, setEditingName] = useState(column.name);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleTitleClick = () => {
    if (isOwner) {
      setIsEditing(true);
      setEditingName(column.name);
    }
  };

  const handleTitleBlur = () => {
    setIsEditing(false);
    const trimmedName = editingName.trim();
    if (trimmedName && trimmedName !== column.name) {
      onMenuAction('rename', trimmedName);
    } else {
      setEditingName(column.name);
    }
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleBlur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditingName(column.name);
    }
  };

  const handleDelete = () => {
    onMenuAction('delete');
  };

  // Task container drag handlers
  const handleTaskContainerDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggedOver(true);
  };

  const handleTaskContainerDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Only set to false if leaving the container entirely
    if (!taskContainerRef.current?.contains(e.relatedTarget as Node)) {
      setIsDraggedOver(false);
    }
  };

  const handleTaskContainerDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggedOver(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'task') {
        // Find the drop position based on the target element
        const target = e.target as HTMLElement;
        const taskCard = target.closest('[data-task-id]') as HTMLElement | null;

        let destIndex = tasks.length;

        if (taskCard) {
          const targetTaskId = parseInt(taskCard.dataset.taskId || '0', 10);
          const targetIndex = tasks.findIndex(t => t.work_item_id === targetTaskId);

          if (targetIndex >= 0) {
            const rect = taskCard.getBoundingClientRect();
            const midY = rect.top + rect.height / 2;
            destIndex = e.clientY < midY ? targetIndex : targetIndex + 1;
          }
        }

        // Adjust index if moving within same column
        if (data.columnId === column.column_id && data.index < destIndex) {
          destIndex -= 1;
        }

        onTaskDrop(data.taskId, data.columnId, column.column_id, destIndex);
      }
    } catch {
      // Ignore parse errors
    }
  };

  // Update editing name when column name changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditingName(column.name);
    }
  }, [column.name, isEditing]);

  const hasTasks = tasks.length > 0;

  return (
      <Paper
        ref={columnRef}
        sx={{
          minWidth: { xs: '85vw', sm: 300 },
          maxWidth: { xs: '85vw', sm: 300 },
          width: { xs: '85vw', sm: 300 },
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        {/* Column Header */}
        <Box
          sx={{
            p: { xs: 1, sm: 1.5 },
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {isEditing ? (
              <TextField
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyDown={handleTitleKeyPress}
                autoFocus
                variant="standard"
                size="small"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    py: 0.5,
                  },
                }}
              />
            ) : (
              <Typography
                variant="h6"
                fontWeight="medium"
                onClick={handleTitleClick}
                sx={{
                  cursor: isOwner ? 'pointer' : 'default',
                  '&:hover': isOwner
                    ? {
                        backgroundColor: 'action.hover',
                        borderRadius: 1,
                        px: 0.5,
                      }
                    : {},
                }}
              >
                {column.name}
              </Typography>
            )}
            <Badge badgeContent={columnTotal} color="primary" sx={{ ml: 1 }} />
          </Box>
          {isOwner && (
            <>
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                sx={{
                  minWidth: { xs: 36, sm: 'auto' },
                  minHeight: { xs: 36, sm: 'auto' },
                  p: { xs: 0.75, sm: 0.5 },
                }}
              >
                <MoreVert sx={{ fontSize: { xs: 18, sm: 24 } }} />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
              >
                <MenuItem
                  onClick={() => { setMenuAnchorEl(null); onMoveLeft?.(); }}
                  disabled={isFirst}
                >
                  <ListItemIcon><ChevronLeft fontSize="small" /></ListItemIcon>
                  <ListItemText>Move left</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => { setMenuAnchorEl(null); onMoveRight?.(); }}
                  disabled={isLast}
                >
                  <ListItemIcon><ChevronRight fontSize="small" /></ListItemIcon>
                  <ListItemText>Move right</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => { setMenuAnchorEl(null); handleDelete(); }}
                  disabled={hasTasks}
                >
                  <ListItemIcon><Close fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText sx={{ color: hasTasks ? undefined : 'error.main' }}>
                    {hasTasks ? 'Delete (has tasks)' : 'Delete column'}
                  </ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {/* Add Task Button - Top */}
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="text"
            size="small"
            onClick={onAddTask}
            sx={{ textTransform: 'none', minHeight: { xs: 44, sm: 36 } }}
          >
            + Add Task
          </Button>
        </Box>

        {/* Task List */}
        <Box
          ref={taskContainerRef}
          onDragOver={handleTaskContainerDragOver}
          onDragLeave={handleTaskContainerDragLeave}
          onDrop={handleTaskContainerDrop}
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 1,
            minHeight: 100,
            backgroundColor: isDraggedOver ? 'action.hover' : 'transparent',
            border: isDraggedOver ? '2px dashed' : 'none',
            borderColor: isDraggedOver ? 'primary.main' : 'transparent',
            borderRadius: isDraggedOver ? 1 : 0,
            transition: 'background-color 0.2s ease, border 0.2s ease',
          }}
        >
          {tasks.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 100,
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">No tasks</Typography>
            </Box>
          ) : (
            <>
              {tasks.map((task, taskIndex) => (
                <TaskCard
                  key={task.work_item_id}
                  task={task}
                  index={taskIndex}
                  columns={columns}
                  onClick={() => onTaskClick(task)}
                />
              ))}
              {tasks.length < columnTotal && (
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={onLoadMore}
                  sx={{ mt: 1, textTransform: 'none', color: 'text.secondary' }}
                >
                  Load more ({columnTotal - tasks.length} remaining)
                </Button>
              )}
            </>
          )}
        </Box>
      </Paper>
  );
};
